import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check } from 'lucide-react';
import './CodeBlock.css';

interface CodeBlockProps {
  children: string;
  language?: string;
  inline?: boolean;
}

/**
 * Code block component with syntax highlighting.
 * Handles both inline code and multi-line code blocks.
 */
export const CodeBlock: React.FC<CodeBlockProps> = ({
  children,
  language,
  inline = false
}) => {
  const [copied, setCopied] = useState(false);

  if (inline) {
    return (
      <code className="inline-code">
        {children}
      </code>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  // Detect language from common patterns if not specified
  const detectedLanguage = language || detectLanguage(children);

  return (
    <div className="code-block-wrapper">
      <div className="code-block-header">
        <span className="code-language" data-lang={detectedLanguage}>
          {detectedLanguage || 'text'}
        </span>
        <button
          className={`code-copy-button ${copied ? 'copied' : ''}`}
          onClick={handleCopy}
          aria-label={copied ? 'Copied!' : 'Copy code'}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          <span>{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
      <SyntaxHighlighter
        language={detectedLanguage || 'text'}
        style={oneDark}
        customStyle={{
          margin: 0,
          borderRadius: '0 0 6px 6px',
          fontSize: '0.875rem',
          padding: '1em',
        }}
        wrapLongLines
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
};

/**
 * Simple language detection for common cases.
 * Falls back to undefined if no pattern matches.
 */
function detectLanguage(code: string): string | undefined {
  const trimmed = code.trim();

  // SQL patterns - most common for Nomi
  if (/^(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|WITH|MERGE|TRUNCATE)\s/i.test(trimmed)) {
    return 'sql';
  }

  // Python patterns
  if (/^(def |class |import |from |if __name__|@\w+|async def )/.test(trimmed)) {
    return 'python';
  }

  // JavaScript/TypeScript patterns
  if (/^(const |let |var |function |import |export |async |interface |type )/.test(trimmed)) {
    return 'typescript';
  }

  // JSON patterns
  if ((trimmed.startsWith('[') || trimmed.startsWith('{')) &&
      (trimmed.endsWith(']') || trimmed.endsWith('}'))) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      // Not valid JSON
    }
  }

  // Bash/shell patterns
  if (/^#!/.test(trimmed) || /^\$\s/.test(trimmed) || /^(echo|cd|ls|mkdir|rm|cp|mv|cat|grep)\s/.test(trimmed)) {
    return 'bash';
  }

  // YAML patterns
  if (/^[\w-]+:\s/.test(trimmed) && !trimmed.includes('{')) {
    return 'yaml';
  }

  return undefined;
}

export default CodeBlock;
